import { TOKEN_H, scaleFor, splitAnchorsFor, weaponMuzzleFor, type Sprite } from '../spriteLayout';
import { spriteOf, corpseOf, weaponOf, scaleOf, corpseScaleOf } from './anchors';
import { fitText } from './text';
import { roundRect } from './hud';
import { drawFighterBody as coreBody, drawFighterWeapon as coreWeapon, fighterGeometry, type FighterDraw, type FighterGeom } from './spriteRender';
import { drawSprite, drawSpriteBox } from './drawSprite';
import { ARMOR, type Action, type ActionKind, type Stance as CombatStance } from '@cs-shot/battle-sim';
import type { Fighter, Scene } from './types';
import type { PlaybackState } from './playbackState';

// name-colour per shooting stance (mirrors the ActionMenu orb rings) + per non-shoot action
const LABEL_SCALE = 1.25; // name / HP / stance labels scaled up with the bigger fighter sprites
const NAME_MAX_W = 132; // name width budget (unscaled px) — just past the HP-bar block; longer → ellipsis
const STANCE_COLOR: Record<CombatStance, string> = { attack: '#ff6a6a', maneuver: '#f2b84b', defense: '#5aa0ff' };
const ACTION_COLOR: Record<string, string> = { knife: '#c2c8d0', grenade: '#57c97a', smoke: '#9aa6a0', flash: '#4fd0c0', medkit: '#5aa0ff' };

// A drawable badge icon: `aspect` (w/h) sizes it; `draw` paints the WHITE glyph centred at
// (cx,cy) with the given height (the coloured disc behind it is drawn by the caller).
interface IconSpec { aspect: number; draw: (ctx: CanvasRenderingContext2D, cx: number, cy: number, size: number) => void; }

// the icons come from the SAME source as the action menu: stances are the menu's Font-Awesome
// glyphs (crosshair / bolt / shield), painted here from their SVG path data so no raster asset
// is needed; every other action reuses the exact white webp the menu shows.
const spriteIcon = (ic: Sprite): IconSpec => ({
  aspect: ic.bw / ic.bh,
  draw: (ctx, cx, cy, size) => {
    const w = size * (ic.bw / ic.bh);
    drawSpriteBox(ctx, ic, cx - w / 2, cy - size / 2, w, size);
  },
});
const vecIcon = (d: string, vw: number, vh: number): IconSpec => {
  const p = new Path2D(d);
  return {
    aspect: vw / vh,
    draw: (ctx, cx, cy, size) => {
      const s = size / vh;
      ctx.save();
      ctx.translate(cx - (vw * s) / 2, cy - size / 2);
      ctx.scale(s, s);
      ctx.fillStyle = '#fff';
      ctx.fill(p);
      ctx.restore();
    },
  };
};
// FaCrosshairs / FaBolt / FaShieldAlt — the same paths src/components/icons/index.tsx renders
const STANCE_ICON: Record<CombatStance, IconSpec> = {
  attack: vecIcon('M500 224h-30.364C455.724 130.325 381.675 56.276 288 42.364V12c0-6.627-5.373-12-12-12h-40c-6.627 0-12 5.373-12 12v30.364C130.325 56.276 56.276 130.325 42.364 224H12c-6.627 0-12 5.373-12 12v40c0 6.627 5.373 12 12 12h30.364C56.276 381.675 130.325 455.724 224 469.636V500c0 6.627 5.373 12 12 12h40c6.627 0 12-5.373 12-12v-30.364C381.675 455.724 455.724 381.675 469.636 288H500c6.627 0 12-5.373 12-12v-40c0-6.627-5.373-12-12-12zM288 404.634V364c0-6.627-5.373-12-12-12h-40c-6.627 0-12 5.373-12 12v40.634C165.826 392.232 119.783 346.243 107.366 288H148c6.627 0 12-5.373 12-12v-40c0-6.627-5.373-12-12-12h-40.634C119.768 165.826 165.757 119.783 224 107.366V148c0 6.627 5.373 12 12 12h40c6.627 0 12-5.373 12-12v-40.634C346.174 119.768 392.217 165.757 404.634 224H364c-6.627 0-12 5.373-12 12v40c0 6.627 5.373 12 12 12h40.634C392.232 346.174 346.243 392.217 288 404.634z', 512, 512),
  maneuver: vecIcon('M296 160H180.6l42.6-129.8C227.2 15 215.7 0 200 0H56C44 0 33.8 8.9 32.2 20.8l-32 240C-1.7 275.2 9.5 288 24 288h118.7L96.6 482.5c-3.6 15.2 8 29.5 23.3 29.5 8.4 0 16.4-4.4 20.8-12l176-304c9.3-15.9-2.2-36-20.7-36z', 320, 512),
  defense: vecIcon('M466.5 83.7l-192-80a48.15 48.15 0 0 0-36.9 0l-192 80C27.7 91.1 16 108.6 16 128c0 198.5 114.5 335.7 221.5 380.3 11.8 4.9 25.1 4.9 36.9 0C360.1 472.6 496 349.3 496 128c0-19.4-11.7-36.9-29.5-44.3zM256.1 446.3l-.1-381 175.9 73.3c-3.3 151.4-82.1 261.1-175.8 307.7z', 512, 512),
};

/** name tint + action icon shown by a fighter's name. */
export interface ActionBadge { color: string; icon: IconSpec | null; }
const iconOf = (sc: Scene, kind: ActionKind, stance: CombatStance): IconSpec | null => {
  switch (kind) {
    case 'shoot': return STANCE_ICON[stance];
    case 'knife': return spriteIcon(sc.utilIcons.knife);
    case 'grenade': return spriteIcon(sc.utilIcons.grenade);
    case 'smoke': return spriteIcon(sc.utilIcons.smoke);
    case 'flash': return spriteIcon(sc.utilIcons.flash);
    case 'medkit': return spriteIcon(sc.utilIcons.medkit);
    default: return null;
  }
};
const badgeFrom = (sc: Scene, kind: ActionKind, stance: CombatStance): ActionBadge =>
  ({ color: kind === 'shoot' ? STANCE_COLOR[stance] : (ACTION_COLOR[kind] ?? '#fff'), icon: iconOf(sc, kind, stance) });

/** badge from the LOCAL player's just-committed Action (immediate, before the round resolves). */
export const actionBadge = (sc: Scene, a: Action | null): ActionBadge | null =>
  a ? badgeFrom(sc, a.kind, a.stance ?? 'attack') : null;

/** badge for ANY fighter from the action they took this round (derived from the replay's events). */
export const fighterBadge = (sc: Scene, f: Fighter): ActionBadge | null =>
  f.roundAction ? badgeFrom(sc, f.roundAction, f.combatStance) : null;

// Build the shared-renderer input for a fighter, or null when the skin has no authored
// split anchors (or its parts aren't loaded) — then the fighter is skipped this frame.
// Per-frame cache of a split fighter's computed draw + geometry, so the weapon pass reuses
// what the body pass already built (same rAF tick) instead of recomputing splitDraw +
// fighterGeometry a second time. Keyed by the frame `time`; a stale entry is ignored.
interface FrameDraw { t: number; d: FighterDraw; g: FighterGeom; }
const frameCache = new WeakMap<Fighter, FrameDraw>();

function splitDraw(sc: Scene, f: Fighter, time: number): FighterDraw | null {
  const A = splitAnchorsFor(f.skin);
  if (!A) return null;
  // knife only while the dash-in/dash-out charge is live — outside the lunge the fighter
  // holds the gun. During it, always the rifle (two-hands-forward) pose, never one-arm awp.
  const knifing = !!f.lunge;
  const stance = knifing ? 'rifle' : f.stance;
  const legs = sc.bodies[`${f.skin}_legs`];
  const upper = sc.bodies[`${f.skin}_upper_${stance}`] ?? sc.bodies[`${f.skin}_upper_rifle`];
  if (!legs || !upper) return null;
  const { bob, sway, recoil } = bodyMotion(f, time);
  return {
    parts: { legs, upper, runFrames: sc.runFrames[f.skin] ?? null, weapon: knifing ? sc.knife : weaponOf(sc, f) },
    A,
    knife: knifing,
    knifeSwing: knifing ? knifeSwing(f, time) : undefined,
    stance,
    x: f.x,
    y: f.y,
    tokenH: TOKEN_H * scaleFor(f.skin),
    dir: f.dir,
    aim: f.aim,
    bob,
    sway,
    recoilX: recoil,
    moving: Math.abs(f.targetX - f.x) > 0.5,
    reverseRun: movingBack(f, time, knifing),
    time,
    muzzle: weaponMuzzleFor(f.weaponKey),
  };
}

/** WORLD muzzle point (bullet exit) for a fighter, computed in the SAME split geometry that
 *  draws the weapon — so the flash/tracer leave the drawn barrel tip and honour the per-weapon
 *  muzzle anchor. Replaces the old whole-body approximation. */
export const muzzleOf = (sc: Scene, f: Fighter, time: number): { x: number; y: number } => {
  const d = splitDraw(sc, f, time);
  return d ? fighterGeometry(d).muzzle : { x: f.x, y: f.y };
};


export const drawShadow = (sc: Scene, f: Fighter, time: number) => {
  const { ctx } = sc;
  const w = f.dead ? corpseOf(sc, f).bw * corpseScaleOf(sc, f) : spriteOf(sc, f).bw * scaleOf(sc, f);
  const deathT = f.dead ? time - f.deadAt : 0;
  ctx.save();
  ctx.globalAlpha = f.dead ? Math.max(0, 0.32 - deathT * 0.3) : 0.32;
  ctx.fillStyle = '#000';
  ctx.beginPath();
  ctx.ellipse(f.x, f.y - 2, w * 0.4, w * 0.15, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
};


// knife slash arc: the blade cocks BACK over the approach, then chops FORWARD into the target,
// landing the chop exactly at the apex (p=0.5, where the strike fires) — so at the moment of
// impact the knife is swung toward the target, not still cocked back. Eases out on the retreat.
// No lunge → knife just rides in the hand (0 rotation).
const KNIFE_RAISE = -0.8; // radians the blade is cocked back during the wind-up
const KNIFE_CHOP = 1.15; // radians it swings forward, into the target, at the strike apex
const knifeSwing = (f: Fighter, time: number): number => {
  const lg = f.lunge;
  if (!lg) return 0;
  const p = Math.max(0, Math.min(1, (time - lg.t0) / lg.dur));
  if (p < 0.35) return KNIFE_RAISE * (p / 0.35); // 0 → cocked back over the approach
  if (p < 0.5) return KNIFE_RAISE + (KNIFE_CHOP - KNIFE_RAISE) * ((p - 0.35) / 0.15); // back → chop INTO target at apex
  return KNIFE_CHOP * (1 - (p - 0.5) / 0.5); // chop → settle back to 0 on the retreat
};

// backpedalling — moving opposite to the facing direction. During a knife lunge the return
// leg (past the apex) is a retreat; otherwise it's whenever the resting spot is BEHIND the
// fighter. The run cycle then plays in reverse so the feet read as walking backward.
const movingBack = (f: Fighter, time: number, knifing: boolean): boolean => {
  if (knifing && f.lunge) return (time - f.lunge.t0) / f.lunge.dur > 0.5;
  return (f.targetX - f.x) * f.dir < 0;
};

// shared body motion so the sprite and its (separately-layered) weapon stay in
// lockstep — the weapon is anchored to the hand, which moves with bob/sway/recoil.
const bodyMotion = (f: Fighter, time: number) => {
  const walking = Math.abs(f.targetX - f.x) > 0.5;
  const bob = walking ? Math.sin(time * 12 + f.phase) * 4 : 0;
  const sway = walking ? Math.sin(time * 9 + f.phase) * 0.05 : 0;
  const recoil = f.recoil * f.dir * -6;
  return { bob, sway, recoil };
};

export const drawFighter = (sc: Scene, f: Fighter, time: number) => {
  const { ctx } = sc;
  if (f.dead) {
    const cp = corpseOf(sc, f);
    const cs = corpseScaleOf(sc, f); // corpse normalized by its own bbox (uniform size)
    const deathT = time - f.deadAt;
    const drop = Math.min(1, deathT * 5);
    const pop = 1 + 0.12 * Math.sin(drop * Math.PI);
    const lift = (1 - drop) * 60 * cs;
    // blood pool spreads on the ground under the corpse (grows as the body settles)
    // variant picked deterministically per fighter (stable across replays)
    let bh = 0; for (let i = 0; i < f.id.length; i++) bh = (bh * 31 + f.id.charCodeAt(i)) | 0;
    const blood = sc.bodies[`blood${1 + (Math.abs(bh) % 6)}`] ?? sc.bodies.blood1; // 6 = BLOOD variants in battleAssets.ts
    if (blood) {
      const spread = Math.min(1, Math.max(0, deathT - 0.15) / 1.4);
      if (spread > 0) {
        const bs = (cp.bw * cs * 0.8 * spread) / blood.bw;
        // mirror ~half the pools (a different hash bit than the corpse flip, so the
        // two are uncorrelated) for extra variety
        const bloodFlip = (Math.abs(bh) >> 1) & 1 ? -1 : 1;
        ctx.save();
        ctx.globalAlpha = 0.92 * spread;
        ctx.translate(f.x, f.y - 2);
        ctx.scale(bs * bloodFlip, bs);
        drawSprite(ctx, blood, -(blood.bx + blood.bw / 2), -(blood.by + blood.bh / 2));
        ctx.restore();
      }
    }
    // corpse stays fully visible until the battle ends (no fade-out)
    // small per-corpse tilt (deterministic from the same hash) so bodies don't
    // all lie at the exact same angle — kept subtle (~±9°)
    const tilt = ((Math.abs(bh) % 100) / 100 - 0.5) * 0.32;
    // mirror ~half the corpses (deterministic per fighter) so bodies don't all
    // face the same way — more varied corpse layout
    const flip = (Math.abs(bh) & 1) ? -1 : 1;
    ctx.save();
    ctx.translate(f.x, f.y - lift - cp.bh * cs * 0.22);
    ctx.rotate(tilt);
    ctx.scale(cs * f.dir * flip * pop, cs * pop);
    drawSprite(ctx, cp, -(cp.bx + cp.bw / 2), -(cp.by + cp.bh / 2));
    ctx.restore();
    return;
  }
  // split body drawn by the SHARED renderer (same code the editor previews use). Cache the
  // draw + geometry so the weapon pass reuses it this frame instead of recomputing.
  const d = splitDraw(sc, f, time);
  if (d) {
    const g = fighterGeometry(d);
    coreBody(ctx, d, g);
    frameCache.set(f, { t: time, d, g });
  }
  // the flashbang glare is NOT drawn here — the weapon is a later pass (drawFighterWeapon)
  // that layers above all bodies, so a glare here would leave the gun un-washed. It's drawn
  // in the weapon pass instead, so it covers body + weapon together (see drawBlindGlare).
};

const BLIND_FADE_S = 0.8; // seconds the glare takes to ease off once its coverage ends
// white flashbang glare washing over a blinded fighter — round-anchored in `blindGlows`, the
// SINGLE owner from the flash pop to expiry (mirrors the smoke screen). A bright initial POP,
// then a STEADY glow held while standing through its whole coverage (no time-based tail-out
// mid-round), then an ease-out over BLIND_FADE_S once `fadeAt` is stamped. Drawn additively
// (lighter) in the WEAPON pass, AFTER the gun, so it covers the body AND the weapon.
const drawBlindGlare = (sc: Scene, f: Fighter, time: number, bob: number, state: PlaybackState) => {
  const g = state.blindGlows.get(f.id);
  if (!g || time < g.bornAt) return;
  const since = time - g.bornAt;
  const pop = Math.max(0, 1 - since / 0.5); // 1→0 over the first 0.5s (the blinding flash)
  let ga = 0.4 + pop * 0.4; // ~0.8 peak → 0.4 steady while standing
  if (g.fadeAt != null) {
    // coverage over (stamped by scheduleRound once `until` < the round): ease off over
    // BLIND_FADE_S — never a hard blink — then drop the entry.
    const k = (time - g.fadeAt) / BLIND_FADE_S;
    if (k >= 1) { state.blindGlows.delete(f.id); return; }
    ga *= 1 - Math.max(0, k);
  }
  const { ctx } = sc;
  const bh = spriteOf(sc, f).bh * scaleOf(sc, f);
  const gy = f.y + bob - bh * 0.62;
  const rad = bh * 0.82;
  const grd = ctx.createRadialGradient(f.x, gy, 0, f.x, gy, rad);
  grd.addColorStop(0, `rgba(255,255,255,${ga})`);
  grd.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  ctx.fillStyle = grd;
  ctx.beginPath();
  ctx.arc(f.x, gy, rad, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
};

// amber ADRENALINE aura — a gentle pulsing glow while the fighter's overshield holds.
// Public info (both sides see it): shooting into the glow feeds the shield, not HP.
const drawAdrenalineGlow = (sc: Scene, f: Fighter, time: number, bob: number) => {
  if (f.shield <= 0 || f.dead) return;
  const { ctx } = sc;
  const bh = spriteOf(sc, f).bh * scaleOf(sc, f);
  const gy = f.y + bob - bh * 0.55;
  const rad = bh * 0.68;
  const ga = 0.16 + 0.06 * Math.sin(time * 5); // soft pulse, well under the blind wash
  const grd = ctx.createRadialGradient(f.x, gy, rad * 0.3, f.x, gy, rad);
  grd.addColorStop(0, `rgba(247,185,62,${ga})`);
  grd.addColorStop(1, 'rgba(247,185,62,0)');
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  ctx.fillStyle = grd;
  ctx.beginPath();
  ctx.arc(f.x, gy, rad, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
};

// the weapon layer — drawn after every body so a neighbour's sprite never clips it
export const drawFighterWeapon = (sc: Scene, f: Fighter, time: number, state: PlaybackState) => {
  if (f.dead) return;
  // reuse the split draw + geometry the body pass already built THIS frame — no 2nd splitDraw
  // / fighterGeometry. bob rides along on the cached FighterDraw (same bodyMotion input).
  const cached = frameCache.get(f);
  if (cached && cached.t === time) {
    coreWeapon(sc.ctx, cached.d, cached.g);
    drawAdrenalineGlow(sc, f, time, cached.d.bob ?? 0);
    drawBlindGlare(sc, f, time, cached.d.bob ?? 0, state);
    return;
  }
  const d = splitDraw(sc, f, time);
  if (d) {
    coreWeapon(sc.ctx, d, fighterGeometry(d));
    drawAdrenalineGlow(sc, f, time, d.bob ?? 0);
    drawBlindGlare(sc, f, time, d.bob ?? 0, state);
  }
};

// per-fighter label above the head: name + HP bar + armor bar.
// Bars are right-leaning parallelograms (slanted ends), like the mockup.
// full bar = the most armor anyone can buy (heavy = 80), so a heavy vest reads as a FULL bar
// instead of ~67% under a hardcoded 120. Derived from config so it tracks any armor rebalance.
const MAX_ARMOR = Math.max(...Object.values(ARMOR).map((a) => a.points));
const BAR_SKEW = 7;
const para = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number) => {
  ctx.beginPath();
  ctx.moveTo(x + BAR_SKEW, y);
  ctx.lineTo(x + w, y);
  ctx.lineTo(x + w - BAR_SKEW, y + h);
  ctx.lineTo(x, y + h);
  ctx.closePath();
};
// number ON the bar, hugging its OUTER end (left end for right-side fighters, right end for
// left-side) — white with a dark outline so it reads over both the coloured fill and the track
const drawBarNumber = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, label: string, labelLeft: boolean, fontPx: number) => {
  ctx.font = `800 ${fontPx}px system-ui, sans-serif`;
  ctx.textAlign = labelLeft ? 'left' : 'right';
  ctx.textBaseline = 'middle';
  ctx.lineWidth = 3.5;
  const inset = Math.round(h * 0.5);
  const tx = labelLeft ? x + inset : x + w - inset;
  const ty = y + h / 2 + 1;
  ctx.strokeStyle = 'rgba(0,0,0,0.7)';
  ctx.strokeText(label, tx, ty);
  ctx.fillStyle = '#fff';
  ctx.fillText(label, tx, ty);
};
// draws the track + coloured fill. `withLabel` false leaves the number for the caller to draw
// LAST (the HP bar defers it so the adrenaline overlay can't paint over the digits).
const drawStatBar = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, frac: number, color: string, label: string, labelLeft: boolean, fontPx = 19, withLabel = true) => {
  // dark track
  ctx.fillStyle = 'rgba(0,0,0,0.6)';
  para(ctx, x - 1, y - 1, w + 2, h + 2);
  ctx.fill();
  // coloured fill (slanted leading edge)
  const fw = Math.max(BAR_SKEW + 1, w * Math.min(1, Math.max(0, frac)));
  ctx.fillStyle = color;
  para(ctx, x, y, fw, h);
  ctx.fill();
  if (withLabel) drawBarNumber(ctx, x, y, w, h, label, labelLeft, fontPx);
};
// compact red "AFK" chip drawn right after a clamped fighter's name (spec §4 — visible to
// teammates and enemies, cleared the moment the server clears the flag). Canvas has no i18n,
// so the literal reads the same in every locale — same treatment as the "T"/"CT" side letters.
const drawAfkTag = (ctx: CanvasRenderingContext2D, x: number, y: number, fontPx: number) => {
  ctx.save();
  ctx.font = `800 ${fontPx}px system-ui, sans-serif`;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  const padX = fontPx * 0.45;
  const h = fontPx * 1.5;
  const w = ctx.measureText('AFK').width + padX * 2;
  ctx.fillStyle = '#e5484d';
  roundRect(ctx, x, y - h / 2, w, h, h / 2);
  ctx.fill();
  ctx.fillStyle = '#fff';
  ctx.fillText('AFK', x + padX, y + 1);
  ctx.restore();
};

export const drawFighterLabel = (sc: Scene, f: Fighter, isYou = false, badge: ActionBadge | null = null) => {
  const { ctx } = sc;
  // body sprite stays uniform for everyone; only YOUR name + HP/armor bars scale
  // up so your fighter is easy to read at a glance
  const k = (isYou ? 1.4 : 1) * LABEL_SCALE;
  const r = (n: number) => Math.round(n * k);
  // label (name + HP/armor bars) stays after death, but drops down toward the corpse
  const top = f.y - spriteOf(sc, f).bh * scaleOf(sc, f);
  const bw = r(104);
  const lx = f.x - bw / 2 - 8;
  const g = r(11);
  const nameY = top - r(64) + (f.dead ? 96 : 0);
  ctx.save();
  ctx.font = `800 ${r(26)}px system-ui, sans-serif`;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.lineWidth = 4;
  ctx.strokeStyle = 'rgba(0,0,0,0.6)';
  // clip a long name to the label block (+a little) with an ellipsis so it can't run over
  // neighbouring fighters or off the arena edge — canvas text has no CSS truncate
  const name = fitText(ctx, f.name, r(NAME_MAX_W));
  ctx.strokeText(name, lx + g, nameY);
  // name is tinted by the fighter's chosen stance/action; a corpse keeps a plain white name
  const live = f.dead ? null : badge;
  ctx.fillStyle = live?.color ?? '#fff';
  ctx.fillText(name, lx + g, nameY);
  if (f.afk) {
    const nameFontPx = r(26);
    drawAfkTag(ctx, lx + g + ctx.measureText(name).width + r(8), nameY + nameFontPx * 0.62, r(15));
  }
  // bars only while alive — a corpse keeps just its name. right-side (ct)
  // fighters show the number LEFT of the bar
  if (!f.dead) {
    const labelLeft = f.team === 'ct';
    const barH = r(13);
    const fontPx = r(19);
    const barTop = nameY + r(28);
    const hpLabel = `${Math.round(Math.max(0, f.hp))}`;
    drawStatBar(ctx, lx + g, barTop, bw, barH, f.hp / f.maxHp, '#39d05a', hpLabel, labelLeft, fontPx, false);
    // ADRENALINE overshield: an amber segment continuing the green HP fill — the shield
    // visibly burns down (amber first) before HP does. Clamped to the bar (maxHp) width;
    // at full HP the pulsing body glow carries the signal instead.
    if (f.shield > 0) {
      const from = Math.min(1, Math.max(0, f.hp / f.maxHp));
      const frac = Math.min(1 - from, f.shield / f.maxHp);
      if (frac > 0.005) {
        ctx.fillStyle = '#f7b93e';
        // start one BAR_SKEW earlier so the amber's slanted leading edge butts flush against the
        // green HP fill's slanted trailing edge (both skew the same way) — no gap between them.
        para(ctx, lx + g + bw * from - BAR_SKEW, barTop, Math.max(BAR_SKEW + 1, bw * frac + BAR_SKEW), barH);
        ctx.fill();
      }
    }
    // HP number LAST — over the fill AND the adrenaline overlay, so the amber never buries it.
    drawBarNumber(ctx, lx + g, barTop, bw, barH, hpLabel, labelLeft, fontPx);
    if (f.armor > 0) {
      drawStatBar(ctx, lx + g, nameY + r(45), bw, barH, f.armor / MAX_ARMOR, '#3b82e7', `${f.armor}`, labelLeft, fontPx);
    }
    // action badge flanks the bars on the OUTER edge (the number sits on the inner one):
    // left of the bars for T, right for CT — a disc in the ACTION's colour with the menu's
    // WHITE glyph on top.
    if (badge?.icon) {
      const rad = r(20);
      const barBottom = nameY + (f.armor > 0 ? r(58) : r(41));
      const cy = (barTop + barBottom) / 2;
      const cx = labelLeft ? lx + g + bw + r(10) + rad : lx + g - r(10) - rad;
      ctx.beginPath();
      ctx.arc(cx, cy, rad, 0, Math.PI * 2);
      ctx.fillStyle = badge.color;
      ctx.fill();
      ctx.lineWidth = r(2);
      ctx.strokeStyle = 'rgba(0,0,0,0.5)';
      ctx.stroke();
      badge.icon.draw(ctx, cx, cy, r(24));
    }
    // remaining-consumable pips — read the enemy's kit at a glance. Only what's still
    // in hand is drawn (empty slots omitted), so it stays sparse even in 5v5.
    const pips: Sprite[] = [];
    if (f.hasGrenade) pips.push(sc.utilIcons.grenade);
    if (f.hasSmoke) pips.push(sc.utilIcons.smoke);
    if (f.hasFlash) pips.push(sc.utilIcons.flash);
    if (f.hasMedkit) pips.push(sc.utilIcons.medkit);
    if (pips.length) {
      const ph = r(16);
      const pgap = r(4);
      const py = nameY + (f.armor > 0 ? r(62) : r(45));
      ctx.globalAlpha = 0.85;
      pips.forEach((ic, i) => {
        const s = ph / ic.bh;
        const px = lx + g + i * (ph + pgap);
        drawSpriteBox(ctx, ic, px, py, ic.bw * s, ph);
      });
      ctx.globalAlpha = 1;
    }
  }
  ctx.restore();
};
